import { useEffect, useMemo } from "react";
import { useIonRouter } from "@/app/IonicContextProvider";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSquare } from "lucide-react";
import { IonPage, IonContent, IonImg } from '@ionic/react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import iconImage from "@/assets/images/icon.png";
import { useToast } from "@/hooks/use-toast";
import { authInitializeRequestSchema } from "@/shared/zod";
import { useInitialize, useFcmToken } from "../hooks";
import { deferredDeepLinkService } from "@/lib/deferredDeepLink";
import type { AuthInitializeRequest } from "../types";

export function InitializePage() {
  const router = useIonRouter();
  const { toast } = useToast();
  const fcmToken = useFcmToken();
  const initializeMutation = useInitialize();

  // Get UID from URL params
  const uid = useMemo(() => {
    if (typeof window !== 'undefined' && window.location) {
      const qs = new URLSearchParams(window.location.search);
      const urlUid = qs.get("uid");
      const pendingUid = localStorage.getItem("pendingInviteUid");
      const deferredUid = localStorage.getItem("deferredInviteUid");
      const deferredData = deferredDeepLinkService.getDeferredData();
      
      // Priority: URL param > pendingInviteUid > deferredInviteUid > deferred service data
      return urlUid || pendingUid || deferredUid || deferredData?.uid || "";
    }
    return localStorage.getItem("pendingInviteUid") || localStorage.getItem("deferredInviteUid") || "";
  }, []);

  useEffect(() => {
    if (uid) {
      localStorage.removeItem("pendingInviteUid");
      localStorage.removeItem("deferredInviteUid");
      deferredDeepLinkService.clearDeferredData();
    }
  }, [uid]);

  const form = useForm<AuthInitializeRequest>({
    resolver: zodResolver(authInitializeRequestSchema),
    defaultValues: {
      whatsapp_number: "",
      pin_code: "",
      fcm_token: fcmToken,
      unique_identifier: uid,
    },
  });

  const onSubmit = async (data: AuthInitializeRequest) => {
    try {
      await initializeMutation.mutateAsync({
        ...data,
        fcm_token: fcmToken,
      });
    } catch (error) {
      toast({
        title: "Initialization Failed",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred during initialization",
        variant: "destructive",
      });
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="min-h-screen bg-muted/30" data-testid="initialize-page">
          <div className="flex min-h-screen">
            <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
              <div className="mx-auto w-full max-w-sm lg:w-96">
                <div className="text-center mb-8">
                  <div className="mx-auto h-32 w-32 bg-white rounded-xl flex items-center justify-center mb-4">
                    <IonImg src={iconImage} />
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-foreground">
                    Welcome to TightShip
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Set up your account to get started
                  </p>
                </div>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <Label htmlFor="whatsapp-number">Phone Number</Label>
                    <Input
                      id="whatsapp-number"
                      type="tel"
                      placeholder="+254712345678"
                      {...form.register("whatsapp_number")}
                      data-testid="input-whatsapp-number"
                    />
                    {form.formState.errors.whatsapp_number && (
                      <p className="mt-1 text-xs text-destructive">
                        {form.formState.errors.whatsapp_number.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="pin-code">Create 4-Digit PIN</Label>
                    <Input
                      id="pin-code"
                      type="password"
                      maxLength={4}
                      placeholder="••••"
                      className="text-center text-2xl tracking-widest"
                      {...form.register("pin_code")}
                      data-testid="input-pin-code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      This PIN will be used for future logins
                    </p>
                    {form.formState.errors.pin_code && (
                      <p className="mt-1 text-xs text-destructive">
                        {form.formState.errors.pin_code.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="unique-identifier">Unique Identifier</Label>
                    <Input
                      id="unique-identifier"
                      readOnly
                      className="bg-muted text-muted-foreground"
                      value={uid}
                      data-testid="input-unique-identifier"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Prefilled from invitation link
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={initializeMutation.isPending}
                    data-testid="button-initialize"
                  >
                    {initializeMutation.isPending
                      ? "Initializing..."
                      : "Initialize Account"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => router.push("/login")}
                      className="font-medium text-primary hover:text-primary/80 transition-colors"
                      data-testid="link-login"
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div className="hidden lg:block relative w-0 flex-1">
              <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary via-primary/80 to-primary/60">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-white">
                  <h3 className="text-3xl font-bold mb-4">
                    Streamline Your Workflow
                  </h3>
                  <p className="text-lg opacity-90 max-w-md">
                    Manage tasks and communicate with your team efficiently in one
                    unified platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
